/**
 * The platform layer — the ONE place the app may know what it is running in.
 *
 * MyDeck ships from one codebase to three targets (docs/MOBILE-PLAN.md): the
 * desktop web app, the iPhone home-screen app (a PWA) and the Android app
 * (Capacitor). A page never asks which one it is in. It calls a capability
 * here, and here decides whether that becomes a native call, a web
 * equivalent, or nothing.
 *
 * 1. **Capacitor may be imported in this directory and nowhere else.**
 *    `eslint.config.js` enforces it: a page importing a plugin directly breaks
 *    the web build, which has no such module.
 * 2. **Every capability does nothing off-native — it never throws.** The
 *    desktop web app keeps running exactly the code it ran before.
 *
 * Detection reads the `Capacitor` global that the Android shell injects,
 * rather than importing `@capacitor/core`, so this module compiles and is
 * correct before Capacitor is installed (mobile phase M2).
 */

/** The slice of the Capacitor runtime global this module reads. */
type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
};

function capacitor(): CapacitorGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as Window & { Capacitor?: CapacitorGlobal }).Capacitor;
}

/** True inside the Android app. False in every browser, installed or not. */
export function isNative(): boolean {
  return capacitor()?.isNativePlatform?.() === true;
}

/** True in the Android app specifically — kept apart from `isNative()` so a
 *  future second native target cannot inherit Android-only behaviour. */
export function isAndroidApp(): boolean {
  return isNative() && capacitor()?.getPlatform?.() === "android";
}

/** True when opened from the home screen as an installed web app, rather than
 *  in a browser tab. `navigator.standalone` is the only signal iPhone Safari
 *  gives; `display-mode` covers everything else. */
export function isInstalledPWA(): boolean {
  if (typeof window === "undefined") return false;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    window.matchMedia?.("(display-mode: fullscreen)").matches === true;
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || iosStandalone;
}

/** True on an iPhone or iPad — the target that gets the home-screen app.
 *  iPadOS reports itself as a Mac, so the touch-point check is what catches
 *  an iPad at all. */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

/** What this client is, in one word. */
export type ClientType = "android" | "pwa" | "web";

export function clientType(): ClientType {
  if (isNative()) return "android";
  return isInstalledPWA() ? "pwa" : "web";
}

/**
 * Where the server is, prepended to every API path.
 *
 * In a browser the app and the API share one origin (FastAPI serves the SPA in
 * production; Vite proxies `/api` in dev), so this is `""` and every request
 * is exactly what it was before the mobile work.
 *
 * Inside the Android app, pages load from the files on the phone
 * (`https://localhost`), so a relative `/api` would ask the phone. The Android
 * build stamps the real server in at build time (`MYDECK_NATIVE_API_ORIGIN`,
 * `vite.config.ts`, `--mode android` only); the web build carries `""` and
 * never reads it.
 */
export function apiBase(): string {
  return isNative() ? __MYDECK_NATIVE_API_ORIGIN__ : "";
}
